import React, { useState, useRef, useCallback } from 'react';
import type { TeamChatMessage } from '@troia-v3/types';
import { Icon } from '@/shared/icons'
import { CHAT_FILE_INPUT_ACCEPT } from '@/modules/chat/utils/acceptedFileTypes';
interface Props {
  onSend: (payload: {
    type: TeamChatMessage['type'];
    content?: string;
    mediaUrl?: string;
    mediaName?: string;
    mediaMimeType?: string;
    mediaSize?: number;
  }) => void;
  onUpload: (file: File) => Promise<void>;
  onTypingChange: (isTyping: boolean) => void;
}

export const TeamChatInput: React.FC<Props> = ({ onSend, onUpload, onTypingChange }) => {
  const [text, setText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    onTypingChange(true);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => onTypingChange(false), 2000);
  };

  const handleSendText = () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    onSend({ type: 'text', content: trimmed });
    setText('');
    onTypingChange(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendText();
    }
  };

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setIsUploading(true);
      try {
        await onUpload(file);
      } finally {
        setIsUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [onUpload]
  );

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Fallback por browser (mesma cadeia do widget): Safari < 18.4 não grava
      // webm/opus — o mimeType hardcoded fazia o construtor explodir e cair no
      // catch ("Microphone not available"), bloqueando gravação em iPhone/Mac.
      // Safari grava audio/mp4 (m4a); o backend converte ogg/webm pra MP3.
      const mimeCandidates = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/mp4',
      ];
      const supportedMime = mimeCandidates.find((m) => MediaRecorder.isTypeSupported(m)) ?? '';
      const recorder = supportedMime
        ? new MediaRecorder(stream, { mimeType: supportedMime })
        : new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        // Extensão e mimetype SEMPRE derivados dos bytes reais gravados
        const mimeType = supportedMime || 'audio/webm';
        const ext = mimeType.includes('mp4') ? 'm4a' : mimeType.includes('ogg') ? 'ogg' : 'webm';
        const blob = new Blob(chunksRef.current, { type: mimeType });
        const file = new File([blob], `audio-${Date.now()}.${ext}`, { type: blob.type });
        setIsUploading(true);
        try {
          await onUpload(file);
        } finally {
          setIsUploading(false);
        }
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
    } catch {
      // Microphone not available
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    resize: 'none',
    fontSize: 13,
    backgroundColor: 'transparent',
    outline: 'none',
    border: 'none',
    color: '#1e293b',
    lineHeight: 1.5,
    maxHeight: 80,
    overflow: 'auto',
    fontFamily: 'inherit',
  };

  const iconButtonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    color: '#64748b',
    padding: 0,
    flexShrink: 0,
  };

  return (
    <div
      style={{
        borderTop: '1px solid rgba(0,0,0,0.08)',
        padding: '8px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <textarea
        value={text}
        onChange={handleTextChange}
        onKeyDown={handleKeyDown}
        placeholder="Digite uma mensagem..."
        rows={1}
        style={inputStyle}
        disabled={isUploading || isRecording}
      />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* File attachment */}
          <button
            style={iconButtonStyle}
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading || isRecording}
            title="Enviar arquivo"
          >
            <Icon name="paperclip" size={15} />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: 'none' }}
            onChange={handleFileChange}
            accept={CHAT_FILE_INPUT_ACCEPT}
          />

          {/* Audio recording */}
          <button
            style={{
              ...iconButtonStyle,
              color: isRecording ? '#ef4444' : '#64748b',
            }}
            onClick={isRecording ? stopRecording : startRecording}
            disabled={isUploading}
            title={isRecording ? 'Parar gravação' : 'Gravar áudio'}
          >
            {isRecording ? <Icon name="square" size={14} /> : <Icon name="mic" size={15} />}
          </button>

          {isUploading && (
            <span style={{ fontSize: 11, color: '#8b5cf6', marginLeft: 4 }}>Enviando...</span>
          )}
          {isRecording && (
            <span style={{ fontSize: 11, color: '#ef4444', marginLeft: 4 }}>Gravando...</span>
          )}
        </div>

        {/* Send button */}
        <button
          onClick={handleSendText}
          disabled={!text.trim() || isUploading}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: '50%',
            border: 'none',
            cursor: text.trim() ? 'pointer' : 'default',
            backgroundColor: text.trim() ? '#8b5cf6' : '#e2e8f0',
            color: '#ffffff',
            padding: 0,
            flexShrink: 0,
            transition: 'background-color 0.15s',
          }}
        >
          <Icon name="send" size={13} />
        </button>
      </div>
    </div>
  );
};
